import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './../Styles/Register.css';
import './../Styles/LandingPage.css';
import logoLight from './../Images/tsqs-hm-ft-logo1.gif';
import logoDark from './../Images/tsqs-hm-ft-logo.gif';
import { Link, useNavigate } from 'react-router-dom';
import { useToast } from './ToastContext';
import { useTheme } from './ThemeContext';
import { FiSun, FiMoon, FiEye, FiEyeOff, FiChevronDown } from 'react-icons/fi';

const apiUrl = process.env.REACT_APP_API_URL;

const Register = () => {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [projects, setProjects] = useState([]);
  
  const showToast = useToast();

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/fetch-projects`);
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };

    fetchProjects();
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();

    const registrationData = {
      username,
      password,
      employeeId,
      projectId: selectedProjectId,
    };

    if (!username || !password || !employeeId || !selectedProjectId) {
      showToast('All fields are required', 'error');
      return;
    }

    const emailDomain = username.split('@')[1];
    if (emailDomain !== 'tsqsinc.com') {
      showToast('Invalid email address. Only tsqsinc.com email addresses are allowed.', 'error');
      return;
    }

    try {
      const validationResponse = await axios.post(`${apiUrl}/api/validate-employee`, {
        employeeId,
        email: username,
        projectId: selectedProjectId
      });
       
      if (validationResponse.data.valid) {
        await axios.post(`${apiUrl}/api/register`, registrationData);
        showToast('Registration was successful', 'success');
        setUsername('');
        setPassword('');
        setEmployeeId('');
        setSelectedProjectId('');
      } else {
        showToast('Employee ID or Email does not exist in our organization.', 'error');
      }
    } catch (error) {
      if (error.response) {
        console.error('Error response:', error.response);
        if (error.response.status === 409 && error.response.data.error === 'User already exists.') {
          showToast('User already exists. Please use a different username.', 'error');
        } else {
          showToast(`Registration failed: ${error.response.data}`, 'error');
        }
      } else if (error.request) {
        showToast('Registration failed: No response from server', 'error');
      } else {
        showToast(`Registration failed: ${error.message}`, 'error');
      }
      console.error('Registration error:', error);
    }
  };

  return (
    <div className="register-page">
      <header className="lp-header" style={{ width: '100%', boxSizing: 'border-box' }}>
        <div className="lp-logo-container">
          <>
            <img src={logoLight} alt="TSQS Logo" className="lp-logo logo-light" />
            <img src={logoDark} alt="TSQS Logo" className="lp-logo logo-dark" />
          </>
        </div>
        <div className="lp-title">
          <h1 className="lp-wordmark" aria-label="Tconnect">
            <span className="lp-wordmark-t">T</span>
            <span className="lp-wordmark-connect">connect</span>
          </h1>
        </div>
        <div className="lp-nav-controls">
          {/* THEME TOGGLE - Uncomment to re-enable dark mode toggle
          <button
            className="lp-theme-toggle"
            onClick={toggleTheme}
            aria-label="Toggle theme"
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {theme === 'dark' ? <FiSun /> : <FiMoon />}
          </button>
          */}
          <button className="lp-btn lp-btn-outline" style={{ padding: '0.4rem 1rem', minWidth: 'auto', fontSize: '0.9rem' }} onClick={() => navigate('/')}>
            Home
          </button>
        </div>
      </header>
      <div className="register-container">
        <div className="register-card">
          <h2>REGISTER</h2>
          <form onSubmit={handleRegister} className="register-form">
            <div className="pms-form-group">
              <label className="pms-label">Email: <span className="required-asterisk">*</span></label>
              <input
                className="pms-input"
                type="email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="pms-form-group">
              <label className="pms-label">Employee ID: <span className="required-asterisk">*</span></label>
              <input
                className="pms-input"
                type="text"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
              />
            </div>
            <div className="pms-form-group" style={{ position: 'relative' }}>
              <label className="pms-label">Password: <span className="required-asterisk">*</span></label>
              <div style={{ position: 'relative' }}>
                <input
                  className="pms-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ width: '100%', paddingRight: '40px' }}
                />
                <span 
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: 'var(--text-secondary)',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </span>
              </div>
            </div>
            <div className="pms-form-group">
              <label className="pms-label">Select Project: <span className="required-asterisk">*</span></label>
              <div style={{ position: 'relative' }}>
                <select 
                  className="pms-input"
                  value={selectedProjectId} 
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  style={{ width: '100%', paddingRight: '40px', cursor: 'pointer' }}
                >
                  <option value="">Select a project</option>
                  {projects.map(project => (
                    <option key={project._id} value={project._id}>
                      {project.project_name}
                    </option>
                  ))}
                </select>
                <span 
                  style={{
                    position: 'absolute',
                    right: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    color: 'var(--text-secondary)',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <FiChevronDown size={18} />
                </span>
              </div>
            </div>
            <button type="submit" className="pms-btn pms-btn-primary register-btn-full">Register</button>
          </form>
          <p className="register-footer-text">
            Return to Login Page, <Link to="/" className="login-link">Click here</Link> to Sign in
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
